import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnButton from "@shared/components/SnButton";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_BAS_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
  });

  const countRef = useRef(0); // 기말재고이월 반영여부

  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_bas_reg_003_01",
        params: searchDataToUse,
      };

      const data: any = await fnGetDataSet(params);
      countRef.current = Number(data[1][0]?.count ?? 0);

      setDataSources({
        grid1: data[0],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onSave
  const onClickSave = async () => {
    try {
      if (countRef.current > 0) {
        const confirm = await showDialog(
          "confirm",
          "이월된 기말재고 정보가 있습니다. 삭제하시고 다시 등록하시겠습니까?"
        );
        if (!confirm) {
          return false;
        }
      } else if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
        await showDialog("alert", "이월할 재고정보가 없습니다.");
        return false;
      }

      const baseinventoryChanges = {
        insert: grids.grid1.dataSource,
        modifiedRowcount: 0,
      };

      baseinventoryChanges.modifiedRowcount = grids.grid1.dataSource.length;

      // 요청 데이터 구성
      const SaveData = {
        baseinventory: baseinventoryChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_BAS_REG_003_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 재조회
      onSearch(null);
      return true;
    } catch (error: any) {
      return false;
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnGrid
          header={[
            <div style={{ paddingLeft: "80px" }}>
              <SnButton
                text={"이월처리"}
                onClick={() => {
                  onClickSave();
                }}
              />
            </div>,
          ]}
          ref={gridRefs.current["grid1"]} // Grid Name
          columnConfig={grids.grid1.columns} // 컬럼 설정 전달
          rowData={grids.grid1.dataSource} // Datasource
          etcProps={{ rowSelection: "single", title: "상세정보" }}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_BAS_REG_003;
